// TUT-450 — os cadastros de assistentes, representantes locais e advogados, e o histórico de contato.
import { demoApi } from "../demo-data";
import type {
  Counsel,
  CounselListResponse,
  ContactHistoryResponse,
  ContactOwnerKind,
  CounselSaveResponse,
  CreateCounselInput,
  CreateExpertInput,
  Expert,
  ExpertKind,
  ExpertListResponse,
  ExpertSaveResponse,
  FiltrosDeCadastro,
  PeritosParaConferirResponse,
  UpdateExpertInput,
  UsuarioDoTutelae,
  VincularPericiasInput,
} from "../types";
import {
  comFatia,
  demoCoverageKey,
  demoLocalRepresentativeFinancialTerms,
  demoLocalRepresentatives,
  request,
} from "./infra";
import type { FatiaPedida } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDosCadastros = {
  // TUT-168 fatia 2: `includeInactive` é OPT-IN. O padrão sem a bandeira é o
  // que alimenta os seletores, e a ficha inativa não pode ser oferecida neles.
  experts(
    search = "",
    includeInactive = false,
    // TUT-171: a lista de quem responde por uma empresa de perícia.
    firmId?: string,
    fatia: FatiaPedida = {},
    // TUT-407: assistente x perito do juízo, filtrado no servidor.
    expertKind?: ExpertKind,
    // TUT-580: Tipo de pessoa, Vínculo, Registro e Portal, também no servidor.
    filtros: FiltrosDeCadastro = {},
  ): Promise<ExpertListResponse> {
    if (useDemoApi)
      return demoApi.experts(search, includeInactive, firmId, fatia, expertKind, filtros);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (includeInactive) params.set("includeInactive", "true");
    if (firmId) params.set("firmId", firmId);
    if (expertKind) params.set("expertKind", expertKind);
    for (const [chave, valor] of Object.entries(filtros)) if (valor) params.set(chave, valor);
    return request<ExpertListResponse>(`/experts${comFatia(params, fatia)}`);
  },
  // TUT-536 (D10): os peritos que só existem como texto livre nas perícias — a
  // lista "para conferir" da tela de Peritos do juízo. Paginada.
  peritosParaConferir(fatia: FatiaPedida = {}): Promise<PeritosParaConferirResponse> {
    if (useDemoApi) return demoApi.peritosParaConferir(fatia);
    return request<PeritosParaConferirResponse>(
      `/experts/para-conferir${comFatia(new URLSearchParams(), fatia)}`,
    );
  },
  // TUT-536: troca o texto livre pela ficha do perito — de UMA perícia (a do
  // formulário) ou de todas com o nome antigo.
  vincularPericias(
    expertId: string,
    input: VincularPericiasInput,
  ): Promise<{ vinculadas: number }> {
    if (useDemoApi) return demoApi.vincularPericias(expertId, input);
    return request<{ vinculadas: number }>(
      `/experts/${encodeURIComponent(expertId)}/vincular-pericias`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  localRepresentatives(city?: string, state?: string) {
    if (useDemoApi) {
      const key = city && state ? demoCoverageKey(city, state) : null;
      return Promise.resolve({
        items: key
          ? demoLocalRepresentatives.filter((representative) =>
              representative.coverageCities.some(
                (coverage) => demoCoverageKey(coverage.city, coverage.state) === key,
              ),
            )
          : demoLocalRepresentatives,
      });
    }
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (state) params.set("state", state);
    const query = params.size ? `?${params.toString()}` : "";
    return request<{ items: import("../types").LocalRepresentative[] }>(
      `/local-representatives${query}`,
    );
  },
  saveLocalRepresentative(
    expertId: string,
    input: import("../types").SaveLocalRepresentativeInput,
  ) {
    if (useDemoApi)
      return demoApi
        .experts("", true, undefined, { page: 1, pageSize: 100 })
        .then((response) => {
          const expert = response.items.find((candidate) => candidate.id === expertId);
          if (!expert) throw new Error("Perito não encontrado.");
          const saved: import("../types").LocalRepresentative = {
            id: expert.id,
            name: expert.name,
            qualification: input.qualification,
            professionalRegistration: input.professionalRegistration,
            centralCity: input.centralCity,
            state: input.state.toUpperCase(),
            contacts: expert.contacts.map((contact) => ({
              id: contact.id,
              kind: contact.kind,
              label: contact.label,
              value: contact.value,
              isPrimary: contact.isPrimary,
            })),
            coverageCities: [
              { city: input.centralCity, state: input.state.toUpperCase(), isCentral: true },
              ...input.coverageCities
                .filter(
                  (coverage) =>
                    demoCoverageKey(coverage.city, coverage.state) !==
                    demoCoverageKey(input.centralCity, input.state),
                )
                .map((coverage) => ({
                  city: coverage.city,
                  state: coverage.state.toUpperCase(),
                  isCentral: false,
                })),
            ],
          };
          // Mudança no lugar (TUT-448): a lista mora no módulo de infra.
          const anterior = demoLocalRepresentatives.findIndex(
            (candidate) => candidate.id === expertId,
          );
          if (anterior !== -1) demoLocalRepresentatives.splice(anterior, 1);
          demoLocalRepresentatives.push(saved);
          // TUT-528 (D8) — a ficha com perfil é a do assistente externo.
          demoApi.tornarExternoDemo(expertId);
          return saved;
        });
    return request<import("../types").LocalRepresentative>(
      `/local-representatives/${encodeURIComponent(expertId)}`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  localRepresentativeFinancialTerms(expertId: string) {
    if (useDemoApi) {
      const terms = demoLocalRepresentativeFinancialTerms.get(expertId);
      if (!terms) return Promise.reject(new Error("Condições financeiras ainda não cadastradas."));
      return Promise.resolve(terms);
    }
    return request<import("../types").LocalRepresentativeFinancialTerms>(
      `/local-representatives/${encodeURIComponent(expertId)}/financial-terms`,
    );
  },
  saveLocalRepresentativeFinancialTerms(
    expertId: string,
    input: { contractedAmountCents: number; excessKmAmountCents: number; paymentTerms: string },
  ) {
    if (useDemoApi) {
      const saved: import("../types").LocalRepresentativeFinancialTerms = {
        expertId, ...input, updatedAt: new Date().toISOString(),
      };
      demoLocalRepresentativeFinancialTerms.set(expertId, saved);
      return Promise.resolve(saved);
    }
    return request<import("../types").LocalRepresentativeFinancialTerms>(
      `/local-representatives/${encodeURIComponent(expertId)}/financial-terms`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  setExpertActive(id: string, active: boolean): Promise<Expert> {
    if (useDemoApi) return demoApi.setExpertActive(id, active);
    return request<Expert>(`/experts/${encodeURIComponent(id)}/active`, {
      method: "POST",
      body: JSON.stringify({ active }),
    });
  },
  deleteExpert(id: string): Promise<void> {
    if (useDemoApi) return demoApi.deleteExpert(id);
    return request<void>(`/experts/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  // TUT-527 (D32) — o "Usuário do Tutelae" do assistente interno.
  usuariosDoTutelae(): Promise<{ items: UsuarioDoTutelae[] }> {
    if (useDemoApi) return demoApi.usuariosDoTutelae();
    return request<{ items: UsuarioDoTutelae[] }>("/experts/usuarios-do-tutelae");
  },
  usuarioDoAssistente(expertId: string): Promise<{ usuario: UsuarioDoTutelae | null }> {
    if (useDemoApi) return demoApi.usuarioDoAssistente(expertId);
    return request<{ usuario: UsuarioDoTutelae | null }>(`/experts/${encodeURIComponent(expertId)}/usuario`);
  },
  vincularUsuarioDoAssistente(
    expertId: string,
    userId: string | null,
  ): Promise<{ usuario: UsuarioDoTutelae | null }> {
    if (useDemoApi) return demoApi.vincularUsuarioDoAssistente(expertId, userId);
    return request<{ usuario: UsuarioDoTutelae | null }>(`/experts/${encodeURIComponent(expertId)}/usuario`, {
      method: "PUT",
      body: JSON.stringify({ userId }),
    });
  },
  createExpert(input: CreateExpertInput): Promise<ExpertSaveResponse> {
    if (useDemoApi) return demoApi.createExpert(input);
    return request<ExpertSaveResponse>("/experts", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // TUT-168 fatia 1: corrigir a ficha. O corpo é a ficha INTEIRA, como na
  // criação — mandar só o campo alterado faria a tela e a API discordarem sobre
  // o que é uma ficha completa.
  updateExpert(
    id: string,
    input: UpdateExpertInput,
  ): Promise<ExpertSaveResponse> {
    if (useDemoApi) return demoApi.updateExpert(id, input);
    return request<ExpertSaveResponse>(`/experts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  counsels(
    search = "",
    includeInactive = false,
    /** TUT-171 — quem responde por este escritório. */
    firmId?: string,
    fatia: FatiaPedida = {},
    // TUT-580: Tipo de pessoa e Portal, no servidor.
    filtros: Pick<FiltrosDeCadastro, "kind" | "portal"> = {},
  ): Promise<CounselListResponse> {
    if (useDemoApi)
      return demoApi.counsels(search, includeInactive, firmId, fatia, filtros);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (includeInactive) params.set("includeInactive", "true");
    if (firmId) params.set("firmId", firmId);
    for (const [chave, valor] of Object.entries(filtros)) if (valor) params.set(chave, valor);
    return request<CounselListResponse>(`/counsels${comFatia(params, fatia)}`);
  },
  setCounselActive(id: string, active: boolean): Promise<Counsel> {
    if (useDemoApi) return demoApi.setCounselActive(id, active);
    return request<Counsel>(`/counsels/${encodeURIComponent(id)}/active`, {
      method: "POST",
      body: JSON.stringify({ active }),
    });
  },
  deleteCounsel(id: string): Promise<void> {
    if (useDemoApi) return demoApi.deleteCounsel(id);
    return request<void>(`/counsels/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  createCounsel(input: CreateCounselInput): Promise<CounselSaveResponse> {
    if (useDemoApi) return demoApi.createCounsel(input);
    return request<CounselSaveResponse>("/counsels", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /**
   * TUT-205c — o histórico de contatos de UMA ficha, nos cinco cadastros.
   *
   * Um método para as cinco rotas, e não cinco métodos: o caminho é a ÚNICA
   * coisa que muda (`/experts/:id/contact-history`, `/counsels/:id/…`, …), e o
   * `ContactOwnerKind` já é a lista fechada desses cinco segmentos. Cinco
   * cópias seriam cinco chances de uma delas esquecer o `encodeURIComponent` ou
   * mandar a fatia por outro nome.
   *
   * A rota é SEPARADA da lista de propósito: `contacts` sai aparado nos
   * vigentes em `GET /experts` e irmãos, porque uma página de 25 fichas muito
   * editadas carregava centenas de contatos que nenhuma tela lia. O histórico é
   * sob demanda, de uma ficha por vez.
   */
  contactHistory(
    owner: ContactOwnerKind,
    id: string,
    fatia: FatiaPedida = {},
  ): Promise<ContactHistoryResponse> {
    if (useDemoApi) return demoApi.contactHistory(owner, id, fatia);
    return request<ContactHistoryResponse>(
      `/${owner}/${encodeURIComponent(id)}/contact-history${comFatia(
        new URLSearchParams(),
        fatia,
      )}`,
    );
  },
  updateCounsel(
    id: string,
    input: CreateCounselInput,
  ): Promise<CounselSaveResponse> {
    if (useDemoApi) return demoApi.updateCounsel(id, input);
    return request<CounselSaveResponse>(`/counsels/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
};
